import dayjs from 'dayjs';

import { Interval } from '@models/graph';

export function createDateAsUTC<T extends Date | null>(date: T): T {
  if (!date) {
    return date;
  }
  return new Date(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds()
    )
  ) as T;
}

export function convertDateToUTC<T extends Date | null>(date: T): T {
  if (!date) {
    return date;
  }
  return new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds()
  ) as T;
}

/**
 * Convert a date object to a string in format yyyymmddThhmmssZ in UTC time
 * @param date Date object to format
 */
export function convertToISOStringNoPunc(
  date: Date,
  options?: { adjustTz?: boolean }
) {
  const adjustTz = options?.adjustTz ?? true;
  let s = dayjs(date).toISOString();
  if (!adjustTz) {
    s = dayjs(createDateAsUTC(dayjs(date).toDate())).toISOString();
  }
  const result = s
    .substring(0, s.indexOf('.'))
    .concat('Z')
    .replace(/:|-/gu, '');
  return result;
}

/**
 * Validate a date object
 * @param date Date object
 * @returns `true` if the date object is valid
 */
export function isValidDate(date: Date) {
  if (Object.prototype.toString.call(date) === '[object Date]') {
    if (!isNaN(date.getTime())) {
      return true;
    }
  }
  return false;
}

/**
 * Validate a time string
 * @param time Time string
 * @returns `true` if the time string is a valid time
 */
export function isValidTime(time: string) {
  // eslint-disable-next-line prefer-named-capture-group
  const re = new RegExp(
    // eslint-disable-next-line prefer-named-capture-group
    /([01][0-9]|2[0-3]):[0-5][0-9]:([0-5][0-9]|60)(\.[0-9]+)?/u,
    'u'
  );
  return re.test(time);
}

/**
 * Combine the date from one `Date` with the time from another `Date`
 * @param date `Date` containing the desired date
 * @param time `Date` containing the desired time
 * @returns A `Date` with the date from `date` and the time from `time`
 */
export function combineDateTime(date: Date, time?: Date): Date {
  const result = new Date(date);
  if (time === undefined) {
    return result;
  }
  result.setHours(time.getHours());
  result.setMinutes(time.getMinutes());
  result.setSeconds(time.getSeconds());
  result.setMilliseconds(time.getMilliseconds());
  return result;
}

/**
 * Round a date object's minutes to the nearest specified interval
 * @param date Date object
 * @param interval Round to nearest `interval` minutes
 * @returns The original date object rounded to the nearest minutes according to interval
 */
export function roundToNearestMinutes(date: Date, interval: number) {
  const roundedMinutes =
    Math.ceil(dayjs(date).get('minutes') / interval) * interval;
  return dayjs(date).startOf('minute').set('minutes', roundedMinutes).toDate();
}

export function calculateAge(date: Date | null): number | null {
  const today = dayjs();
  const birthday = dayjs(date);
  return today.diff(birthday, 'year');
}

export function offsetDate(
  days = 0,
  months = 0,
  years = 0,
  fromDate: Date = new Date()
) {
  return dayjs(fromDate)
    .add(days, 'days')
    .add(months, 'months')
    .add(years, 'years')
    .toDate();
}

export function calculateMinDate(maxDate: string, interval: Interval) {
  return dayjs(maxDate)
    .subtract(interval === Interval.Week ? 7 : 0, 'days')
    .subtract(interval === Interval.Month ? 1 : 0, 'months')
    .subtract(interval === Interval.Year ? 1 : 0, 'years')
    .startOf('day')
    .toISOString();
}

/**
 * Check if a FHIR date string has a meaningful time component.
 * Date-only strings ("2026-01-16") and midnight placeholders ("T00:00:00.000Z")
 * are treated as having no time.
 */
export function hasTimeComponent(dateStr: string | null | undefined): boolean {
  if (!dateStr || !dateStr.includes('T')) return false;
  return !dateStr.endsWith('T00:00:00.000Z') && !dateStr.endsWith('T00:00:00Z');
}
